import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Check, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useSession } from '@/components/auth/session';
import { useShell } from '@/components/layout/shell';
import { Button } from '@/components/ui/button';
import { Surface } from '@/components/ui/section';
import { useBriefs, useRepos, useWorkers } from '@/hooks/use-fleet';
import { api } from '@/lib/api';
import { hubKeys } from '@/lib/query';
import { cn } from '@/lib/utils';

/**
 * The first thing a new person sees, and the only thing on the page that
 * eventually goes away.
 *
 * Dyspatch has a genuine cold-start problem that no amount of empty-state copy
 * fixes: a hub with no enrolled device and no job filed looks exactly like a
 * quiet hub, and both of them look like a broken one. Worse, the two gates that
 * are the whole product cannot be demonstrated until a job exists to gate, so the
 * home page's most important region is empty precisely when somebody is trying to
 * work out what this is.
 *
 * So the checklist is not a tour and not a modal. It is four facts for a member
 * and five for an admin, each read from the same queries the rest of the page
 * uses, each ticking itself off when it becomes true. Nobody marks a step complete; the step is complete when the
 * state says so, which means it cannot lie and it cannot be skipped past into a
 * broken setup.
 *
 * Whose facts, though, is the whole question, and getting it wrong is worse than
 * having no checklist. An admin is setting up a hub, so the hub is the subject:
 * has anyone enrolled a device, has any job been filed. A member is being shown
 * what to do next, so *they* are the subject. On a hub with twenty-one jobs on
 * it, measuring a member against the hub greets them with "Open your first job"
 * and "Approve a plan" already struck through on their first sign-in - a list of
 * things they have not done, congratulating them for doing them. The device step
 * has always read `myDevices` for a member; the two job steps now do the same,
 * because they were the two that could be true about a stranger.
 *
 * The admin's extra step is "Register a repo", and it comes before the job
 * steps because it is what has to be true for them to be possible: a job names
 * the repo it touches, the field is required, and a hub with an empty registry
 * sends every member to a form they cannot submit.
 *
 * It removes itself when they are all true. Until then it can be dismissed, and
 * that choice is per browser, because it is a preference about a person's own
 * screen rather than a fact about the hub.
 *
 * A member sees a member's version: they cannot enroll a device or invite anyone,
 * so their list is the two steps they can actually do.
 */
const DISMISS_KEY = 'dyspatch.setup.dismissed';

function dismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    // A browser with storage blocked still gets the checklist; it just cannot
    // remember that it was dismissed, which is the harmless direction to fail.
    return false;
  }
}

interface Step {
  id: string;
  label: string;
  hint: string;
  done: boolean;
  action?: { label: string; onClick?: () => void; to?: string };
}

function StepRow({ step, index }: { step: Step; index: number }) {
  return (
    <li className="flex items-start gap-3 px-gutter py-3">
      <span
        className={cn(
          'mt-px grid size-5 shrink-0 place-items-center rounded-full text-2xs font-semibold',
          step.done ? 'text-white' : 'border-line-2 text-fg-3 border',
        )}
        style={step.done ? { background: 'var(--dy-ok)' } : undefined}
        aria-hidden
      >
        {step.done ? <Check className="size-3" strokeWidth={3} /> : index + 1}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block text-base font-medium',
            step.done ? 'text-fg-3 line-through decoration-1' : 'text-foreground',
          )}
        >
          {step.label}
        </span>
        {!step.done ? <span className="text-fg-3 block text-sm">{step.hint}</span> : null}
      </span>
      {!step.done && step.action ? (
        <span className="shrink-0">
          {step.action.to ? (
            <Button size="sm" variant="outline" asChild>
              <Link to={step.action.to}>{step.action.label}</Link>
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={step.action.onClick}>
              {step.action.label}
            </Button>
          )}
        </span>
      ) : null}
    </li>
  );
}

export function SetupChecklist() {
  const { session } = useSession();
  const { openCompose } = useShell();
  const [hidden, setHidden] = useState(dismissed);

  // An admin reads the hub, a member reads themselves, and neither asks for the
  // other's query: `/api/briefs` is the whole fleet and is not a member's
  // business to fetch just to tick a box on their own dashboard.
  const briefs = useBriefs(session.isAdmin);
  const workers = useWorkers(session.isAdmin);
  // Which repos exist at all. A read for anybody signed in, and the answer to
  // the step below it: the job composer's Repo field is required, so a member
  // who reaches "Open your first job" on a hub with an empty registry finds a
  // form they cannot submit and nothing saying why.
  //
  // Through `useRepos` rather than a `useQuery` of its own, which is what this
  // was and is why the dashboard crashed: the two share `hubKeys.repos`, and
  // the raw `{ repos }` this one cached was read as the array `useRepos`
  // promises everywhere else on the page.
  const repos = useRepos();
  const myDevices = useQuery({
    queryKey: hubKeys.myDevices,
    queryFn: api.myDevices,
    enabled: !session.isAdmin,
  });
  const myBriefs = useQuery({
    queryKey: hubKeys.myBriefs,
    queryFn: async () => (await api.myBriefs()).briefs,
    enabled: !session.isAdmin,
  });

  const dismiss = useCallback(() => {
    setHidden(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Nothing to do: the checklist closes for this view either way.
    }
  }, []);

  const briefRows = (session.isAdmin ? briefs.data : myBriefs.data) ?? [];
  const deviceCount = session.isAdmin
    ? (workers.data?.length ?? 0)
    : (myDevices.data?.devices.length ?? 0);
  // A plan has been through the first gate if a job ever got past it. `draft`
  // and `decomposing` are before it; `awaiting_approval` is standing at it.
  const approvedOne = briefRows.some(
    (brief) => brief.status === 'approved' || brief.status === 'done',
  );

  const repoCount = repos.data?.length ?? 0;

  const steps: Step[] = [
    {
      id: 'account',
      label: 'Sign in',
      hint: 'You are here.',
      done: true,
    },
    // Admin-only, and second rather than last, because it is the step that has
    // to be true before the one after it can be: a job names a repo, the field
    // is required, and an admin who skips this leaves every member on this hub
    // at a form with an empty picker.
    ...(session.isAdmin
      ? [
          {
            id: 'repo',
            label: 'Register a repo',
            hint: 'A job names the repo it touches, and only registered ones can be picked. This is also where a repo says which checks its PRs have to pass.',
            done: repoCount > 0,
            action: { label: 'Register', to: '/admin/repos' },
          },
        ]
      : []),
    {
      id: 'device',
      label: session.isAdmin ? 'Enroll an agent' : 'Get an agent on your laptop',
      hint: session.isAdmin
        ? 'Agents run on your own laptops under your own Claude subscription. Nothing runs until one is enrolled.'
        : 'Agents run on your own laptop under your own Claude subscription. Connecting one is an install and two commands, and the page waits with you.',
      done: deviceCount > 0,
      // A member had no action here at all, and the hint told them to go and ask
      // an admin for a code. Connecting a laptop is their own business now, and
      // the step points at the card that does it.
      action: { label: session.isAdmin ? 'Enroll' : 'Connect', to: '/agents' },
    },
    {
      id: 'job',
      label: 'Open your first job',
      // The hint has to be able to say why the form cannot be filled in. A
      // member cannot register a repo and should not be sent to a composer
      // whose required field has nothing in it.
      hint:
        repoCount === 0
          ? 'A job names the repo it touches, and this hub has none registered yet. Ask an admin to register one.'
          : 'A title, what should be true when it is done, and the repos it touches.',
      done: briefRows.length > 0,
      ...(repoCount === 0 && !session.isAdmin
        ? {}
        : { action: { label: 'New job', onClick: openCompose } }),
    },
    {
      id: 'gate',
      label: 'Approve a plan',
      hint: 'An agent drafts a plan of tasks. Nothing runs until you say go.',
      done: approvedOne,
    },
  ];

  const complete = steps.filter((step) => step.done).length;
  // Gone for good once the hub is genuinely set up. No preference involved: the
  // card has nothing left to say.
  if (hidden || complete === steps.length) return null;

  return (
    <Surface className="relative">
      <div className="flex items-baseline gap-2.5 px-gutter pt-4 pb-1">
        <h2 className="text-lg font-semibold">Get Dyspatch running</h2>
        <span className="text-fg-3 tnum text-xs">
          {complete} of {steps.length}
        </span>
        <Button
          variant="ghost"
          size="icon-sm"
          className="ml-auto -translate-y-1"
          onClick={dismiss}
          aria-label="Dismiss the setup checklist"
          title="Dismiss. It comes back if you clear this browser's storage."
        >
          <X />
        </Button>
      </div>
      <p className="text-fg-3 px-gutter pb-2 text-sm">
        Work you file becomes a job. An agent drafts a plan, you approve it, the agent works on
        somebody's actual laptop, and you review the pull request it opens. Two of those are yours.
      </p>
      <ul className="divide-line divide-y border-t">
        {steps.map((step, index) => (
          <StepRow key={step.id} step={step} index={index} />
        ))}
      </ul>
    </Surface>
  );
}
